<template>
	<header class="site__header reveal-fade-in" :class="{ 'revealed': isHeaderReady }" :style="{ background: headerBackgroundColor, boxShadow: headerBoxShadow }">
		<div class="header-container">
			<!-- Suntop Logo -->
			<router-link :to="{ name: 'welcome' }" class="brand__logo reveal-fade-in reveal-delay-1" :class="{ 'revealed': isHeaderReady }">
				<img :src="logoPath" alt="Suntop" class="logo-img">
			</router-link>

			<!-- Language Switcher (Right) -->
			<div class="navigation-wrapper reveal-fade-in reveal-delay-3" :class="{ 'revealed': isHeaderReady }">
				<locale-dropdown />
			</div>
		</div>
	</header>
</template>

<script>
import { mapGetters } from 'vuex'
import LocaleDropdown from './LocaleDropdown'

export default {
	layout: 'default',
	components: {
		LocaleDropdown
	},

	data: () => ({
		isHeaderReady: false
	}),

	computed: {
		...mapGetters({
			locale: 'lang/locale',
			currentStep: 'wizard/currentStep'
		}),

		// Dynamic header background color based on wizard step
		headerBackgroundColor() {
			switch (this.currentStep) {
				case 0: // TOS Section (before wizard starts)
				case 1: // Mobile Verification Step
				case 2: // Personal Details Step
					return '#0000FF' // Blue
				case 3: // Spin Wheel Step
					return '#FF004A' // Red/Pink
				case 4: // Prize Reveal Step
					return '#B700CE' // Purple
				default:
					return 'linear-gradient(135deg, #ff4101 0%, #ff4101 50%, #ff4101 100%)' // Default red gradient
			}
		},

		// Dynamic logo path based on wizard step
		logoPath() {
			switch (this.currentStep) {
				case 0: // TOS Section (before wizard starts)
				case 1: // Mobile Verification Step
				case 2: // Personal Details Step
					return '/images/logos/suntop-logo-x-onepiece-bluebg.png' // Blue background logo
				case 3: // Spin Wheel Step
					return '/images/logos/suntop-logo-x-onepiece-redbg.png' // Red background logo
				case 4: // Prize Reveal Step
					return '/images/logos/suntop-logo-x-onepiece-purplebg.png' // Purple background logo
				default:
					return '/images/logo-main.png' // Default logo
			}
		},

		// Dynamic header box-shadow based on wizard step colors
		headerBoxShadow() {
			switch (this.currentStep) {
				case 0: // TOS Section (before wizard starts)
				case 1: // Mobile Verification Step
				case 2: // Personal Details Step
					// Blue shadow (#0000FF)
					return '0 4px 20px rgba(0, 0, 0, 0.3), 0 2px 10px rgba(0, 0, 255, 0.4), inset 0 -3px 0 rgba(0, 0, 204, 0.6)'
				case 3: // Spin Wheel Step
					// Red/Pink shadow (#FF004A)
					return '0 4px 20px rgba(0, 0, 0, 0.3), 0 2px 10px rgba(255, 0, 74, 0.4), inset 0 -3px 0 rgba(204, 0, 59, 0.6)'
				case 4: // Prize Reveal Step
					// Purple shadow (#B700CE)
					return '0 4px 20px rgba(0, 0, 0, 0.3), 0 2px 10px rgba(183, 0, 206, 0.4), inset 0 -3px 0 rgba(146, 0, 165, 0.6)'
				default:
					// Default orange shadow (#ff4101)
					return '0 4px 20px rgba(0, 0, 0, 0.3), 0 2px 10px rgba(233, 90, 11, 0.4), inset 0 -3px 0 rgba(199, 74, 9, 0.6)'
			}
		}
	},

	mounted() {
		// Trigger header reveal animation
		this.$nextTick(() => {
			setTimeout(() => {
				this.isHeaderReady = true;
			}, 50);
		});

		// Also trigger on window load for reliability
		if (document.readyState === 'complete') {
			this.isHeaderReady = true;
		} else {
			window.addEventListener('load', () => {
				setTimeout(() => {
					this.isHeaderReady = true;
				}, 30);
			}, { once: true });
		}

		// Scroll behavior
		window.addEventListener('scroll', this.handleScroll);
	},

	beforeUnmount() {
		window.removeEventListener('scroll', this.handleScroll);
	},

	methods: {
		handleScroll() {
			const header = this.$el;
			if (window.scrollY > 50) {
				header.classList.add('scrolled');
			} else {
				header.classList.remove('scrolled');
			}
		}
	}
}
</script>

<style lang="scss" scoped>
.site {
	&__header {
		direction: ltr !important;
		display: flex;
		align-items: center;
		justify-content: space-between;
		left: 0;
		padding: 0;
		height: 110px;
		position: fixed;
		top: 0;
		width: 100%;
		z-index: 100;

		// Hero-inspired gradient background (default - will be overridden by inline style)
		background: linear-gradient(135deg, #ff4101 0%, #ff4101 50%, #ff4101 100%);
		background-size: 200% 200%;

		// Enhanced shadow for floating effect
		box-shadow:
			0 4px 20px rgba(0, 0, 0, 0.3),
			0 2px 10px rgba(233, 90, 11, 0.4),
			inset 0 -3px 0 rgba(199, 74, 9, 0.6);

		// Smooth transitions for background color changes
		transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1), background 0.6s ease-in-out;

		// Subtle backdrop blur for modern feel
		backdrop-filter: blur(10px);
		-webkit-backdrop-filter: blur(10px);

		// Animated gradient on scroll
		&.scrolled {
			height: 80px;
			box-shadow:
				0 2px 15px rgba(0, 0, 0, 0.4),
				0 1px 8px rgba(233, 90, 11, 0.5);
			background-position: 100% 50%;

			// Shrink logo on scroll
			.logo-img {
				width: clamp(120px, 15vw, 160px) !important;
			}
		}
	}
}

.header-container {
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
	max-width: 1600px;
	margin: 0 auto;
	padding: 0 clamp(20px, 4vw, 60px);
}

.brand {
	&__logo {
		display: flex;
		align-items: center;
		transition: all 0.3s ease;
		flex-shrink: 0;
		text-decoration: none;
		cursor: pointer;

		.logo-img {
			width: clamp(150px, 20vw, 200px);
			height: auto;
			object-fit: contain;
			transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease-in-out;

			&:hover {
				transform: scale(1.05) rotate(1deg);
			}
		}
	}
}

.navigation-wrapper {
	display: flex;
	align-items: center;
	flex-shrink: 0;
}

// Responsive adjustments
@media (max-width: 991px) {
	.site__header {
		height: 80px;

		&.scrolled {
			height: 70px;

			.logo-img {
				width: clamp(100px, 15vw, 140px) !important;
			}
		}
	}

	.brand__logo .logo-img {
		width: clamp(130px, 18vw, 180px);
	}
}

@media (max-width: 600px) {
	.site__header {
		height: 70px;

		&.scrolled {
			height: 65px;

			.logo-img {
				width: clamp(70px, 12vw, 85px) !important;
			}
		}
	}

	.header-container {
		padding: 0 clamp(15px, 4vw, 25px);
	}

	.brand__logo .logo-img {
		width: clamp(80px, 15vw, 100px);
	}
}

// Animation keyframes
@keyframes gradientShift {
	0% {
		background-position: 0% 50%;
	}
	50% {
		background-position: 100% 50%;
	}
	100% {
		background-position: 0% 50%;
	}
}

// Apply subtle gradient animation
.site__header {
	animation: gradientShift 15s ease infinite;
}
</style>